import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Buildings, MapPin, Envelope, Phone, Globe, User, ShieldCheck, House,
} from "@phosphor-icons/react";

export function AgencyPublicPage() {
  const { slug } = useParams();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/agencies/${slug}`);
        if (!cancelled) setAgency(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.status === 404 ? "Esta agencia no existe." : "No pudimos cargar la agencia.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return (
    <div className="max-w-5xl mx-auto px-6 py-16 animate-pulse space-y-4">
      <div className="h-20 w-full bg-[#1A1A1A] rounded-2xl" />
      <div className="h-64 bg-[#1A1A1A] rounded-2xl" />
    </div>
  );

  if (error || !agency) return (
    <div className="max-w-4xl mx-auto px-6 py-24 text-center" data-testid="agency-public-error">
      <Buildings size={40} className="mx-auto text-[#C8A24A]/30 mb-4" />
      <p className="font-heading text-2xl">{error || "Agencia no encontrada"}</p>
      <Link to="/" className="text-sm text-[#C8A24A]/70 hover:text-[#C8A24A] mt-4 inline-block">← Volver al inicio</Link>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-5rem)]" data-testid="agency-public-page">
      {/* Hero */}
      <div className="border-b border-[#C8A24A]/10 bg-gradient-to-b from-[#111111] to-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
          <Link to="/propiedades" className="text-xs text-[#C8A24A]/60 hover:text-[#C8A24A] inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Ver propiedades
          </Link>
          <div className="mt-6 flex flex-col md:flex-row gap-8 items-start">
            <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-[#C8A24A]/40 bg-[#1A1A1A] flex items-center justify-center">
              {agency.logo_url ? (
                <img src={imageUrl(agency.logo_url)} alt={agency.name} className="w-full h-full object-cover" />
              ) : (
                <Buildings size={48} weight="duotone" className="text-[#C8A24A]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Agencia inmobiliaria</p>
              <h1 className="font-heading text-4xl md:text-5xl mt-2 tracking-tight" data-testid="agency-public-name">{agency.name}</h1>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-[#C8A24A]/70">
                {agency.city && (
                  <span className="inline-flex items-center gap-1"><MapPin size={12} weight="fill" /> {[agency.city, agency.state].filter(Boolean).join(", ")}</span>
                )}
                <span className="inline-flex items-center gap-1"><User size={12} weight="fill" /> {agency.members.length} asesor(es)</span>
                <span className="inline-flex items-center gap-1"><House size={12} weight="fill" /> {agency.properties_count} propiedad(es)</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="agency-public-contacts">
                {agency.email && (
                  <a href={`mailto:${agency.email}`} className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[#C8A24A]/40 text-[#C8A24A] hover:bg-[#C8A24A]/10 text-xs font-semibold" data-testid="agency-public-mail">
                    <Envelope size={14} weight="duotone" /> {agency.email}
                  </a>
                )}
                {agency.phone && (
                  <a href={`tel:${agency.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs font-semibold" data-testid="agency-public-tel">
                    <Phone size={14} weight="duotone" /> {agency.phone}
                  </a>
                )}
                {agency.website && (
                  <a href={agency.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[#C8A24A]/40 text-[#C8A24A]/80 hover:bg-[#C8A24A]/10 text-xs font-semibold" data-testid="agency-public-web">
                    <Globe size={14} weight="duotone" /> Sitio
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14 grid md:grid-cols-3 gap-6 md:gap-10">
        <aside className="space-y-6 md:col-span-1">
          {agency.description && (
            <section data-testid="agency-public-desc">
              <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Sobre nosotros</p>
              <p className="text-sm text-[#C8A24A]/90 leading-relaxed whitespace-pre-wrap">{agency.description}</p>
            </section>
          )}
          {agency.coverage_zones?.length > 0 && (
            <section data-testid="agency-public-zones">
              <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2 inline-flex items-center gap-1.5">
                <MapPin size={11} weight="fill" /> Cobertura
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {agency.coverage_zones.map(z => (
                  <li key={z} className="text-xs px-2.5 py-1 rounded-full bg-[#1A1A1A] border border-[#C8A24A]/20 text-[#C8A24A]/90">{z}</li>
                ))}
              </ul>
            </section>
          )}
        </aside>

        <section className="md:col-span-2">
          <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-4">Nuestro equipo</p>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="agency-public-members">
            {agency.members.map(m => {
              const initials = (m.name || "??").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();
              return (
                <Link
                  key={m.id}
                  to={m.slug ? `/asesor/${m.slug}` : "#"}
                  className="p-4 rounded-2xl bg-[#1A1A1A] border border-[#C8A24A]/20 hover:border-[#C8A24A]/40 transition-colors flex items-start gap-3"
                  data-testid={`agency-public-member-${m.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#0A0A0A] border border-[#C8A24A]/20 flex items-center justify-center overflow-hidden">
                    {m.photo_url ? (
                      <img src={imageUrl(m.photo_url)} alt={m.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-heading font-bold text-[#C8A24A]">{initials}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-semibold text-sm truncate">{m.name}</p>
                      {m.agency_role === "owner" && <ShieldCheck size={12} weight="fill" className="text-[#C8A24A] flex-shrink-0" />}
                    </div>
                    {m.specialties?.length > 0 && (
                      <p className="text-xs text-[#C8A24A]/60 truncate mt-0.5">{m.specialties.slice(0, 3).join(" · ")}</p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
